/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from './AuthContext';
import hybridCartService, { 
  HybridCart, 
  GuestCart, 
  UserCart, 
  Product,
  GuestCartItem,
  UserCartItem 
} from '@/services/hybridCartService';

// ============ TYPES ============

interface HybridCartContextType {
  // State
  cart: HybridCart | null;
  loading: boolean;
  isGuest: boolean;

  // Cart Operations
  addToCart: (product: Product, quantity?: number, customization?: { personalizedName?: string; notes?: string }) => Promise<void>;
  updateItemQuantity: (productId: string, quantity: number) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  clearCartSilently: () => Promise<void>;

  // Hybrid Operations
  mergeWithUserCart: (onSuccess?: () => void) => Promise<void>;
  syncGuestCart: () => Promise<void>;

  // Computed Values
  totalItems: number;
  subtotal: number;
  total: number;
  taxes?: number;

  // Utilities
  hasItems: boolean;
  getCartItems: () => Array<GuestCartItem | UserCartItem>;
}

// ============ CONTEXT ============

const HybridCartContext = createContext<HybridCartContextType | undefined>(undefined);

export const useHybridCart = () => {
  const context = useContext(HybridCartContext);
  if (!context) {
    throw new Error('useHybridCart debe ser usado dentro de un HybridCartProvider');
  }
  return context;
};

// ============ NORMALIZATION FUNCTION ============

const normalizeCart = (cart: GuestCart | UserCart | null): HybridCart | null => {
  if (!cart) return null;

  const TAX_RATE = 0.15; // 15%

  const normalizedItems = cart.items.map(item => {
    // For guest cart, product data is at the top level of the item.
    // For user cart, it's nested under the 'product' property.
    const productData = cart.isGuest ? item : item.product;

    const price = productData?.price || 0;
    const quantity = item.quantity || 0;
    const subtotal = price * quantity;

    return {
      ...item,
      name: productData?.name || productData?.title || 'Producto Desconocido',
      image: productData?.image || '/placeholder.svg',
      price,
      subtotal,
      productId: cart.isGuest ? item.productId : productData?._id,
    };
  });

  const subtotal = normalizedItems.reduce((acc, item) => acc + item.subtotal, 0);
  const total = subtotal; // No taxes applied
  const totalItems = normalizedItems.reduce((acc, item) => acc + item.quantity, 0);

  return {
    ...cart,
    items: normalizedItems,
    subtotal,
    taxes: 0, // No taxes
    total,
    totalItems: cart.isGuest ? cart.itemCount : totalItems,
    itemCount: totalItems, // Ensure itemCount is consistent
  };
};


// ============ PROVIDER ============

export const HybridCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mergePromptShown, setMergePromptShown] = useState(false);
  
  // Local state for guest cart, now normalized
  const [guestCart, setGuestCart] = useState<HybridCart | null>(() => 
    normalizeCart(hybridCartService.getGuestCart())
  );
  
  // Server state for user cart, normalized via `select`
  const { data: userCart, isLoading: userCartLoading } = useQuery({
    queryKey: ['userCart'],
    queryFn: hybridCartService.getUserCart,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (rawUserCart) => normalizeCart(rawUserCart),
  });

  // Determine current cart and loading state
  const cart: HybridCart | null = isAuthenticated ? userCart : guestCart;
  const loading = isAuthenticated ? userCartLoading : false;
  const isGuest = !isAuthenticated;

  // ============ GUEST CART OPERATIONS ============

  const updateGuestCart = useCallback((updatedCart: GuestCart) => {
    setGuestCart(normalizeCart(updatedCart));
  }, []);

  const addToGuestCart = useCallback(async (product: Product, quantity: number = 1, customization?: { personalizedName?: string; notes?: string }) => {
    try {
      const updatedCart = hybridCartService.addToGuestCart(product, quantity, customization);
      updateGuestCart(updatedCart);

      toast({
        title: "Producto añadido",
        description: `${product.name} añadido al carrito`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo añadir el producto al carrito",
        variant: "destructive",
      });
      throw error;
    }
  }, [updateGuestCart, toast]);

  const updateGuestCartItem = useCallback(async (productId: string, quantity: number) => {
    try {
      const updatedCart = hybridCartService.updateGuestCartItem(productId, quantity);
      updateGuestCart(updatedCart);
      
      toast({
        title: "Carrito actualizado",
        description: "La cantidad del producto ha sido actualizada",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar la cantidad",
        variant: "destructive",
      });
      throw error;
    }
  }, [updateGuestCart, toast]);

  const removeFromGuestCart = useCallback(async (productId: string) => {
    try {
      const updatedCart = hybridCartService.removeFromGuestCart(productId);
      updateGuestCart(updatedCart);
      
      toast({
        title: "Producto eliminado",
        description: "El producto se ha eliminado del carrito",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el producto",
        variant: "destructive",
      });
      throw error;
    }
  }, [updateGuestCart, toast]);

  const clearGuestCart = useCallback(async () => {
    try {
      const emptyCart = hybridCartService.clearGuestCart();
      updateGuestCart(emptyCart);
      
      toast({
        title: "Carrito vaciado",
        description: "Se han eliminado todos los productos del carrito",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo vaciar el carrito",
        variant: "destructive",
      });
      throw error;
    }
  }, [updateGuestCart, toast]);

  const clearGuestCartSilently = useCallback(async () => {
    try {
      const emptyCart = hybridCartService.clearGuestCart();
      updateGuestCart(emptyCart);
    } catch (error) {
      console.error('Error clearing guest cart silently:', error);
      throw error;
    }
  }, [updateGuestCart]);

  // ============ USER CART MUTATIONS ============

  const addToUserCartMutation = useMutation({
    mutationFn: ({ productId, quantity, customization, previewImage }: { productId: string; quantity: number; customization?: { personalizedName?: string; notes?: string }; previewImage?: string }) =>
      hybridCartService.addToUserCart(productId, quantity, customization, previewImage),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userCart'] });

      // Find the product name from the returned cart (con validación defensiva)
      const addedItem = data.items.find(item => item.product?._id === variables.productId);
      const productName = addedItem?.product?.name || addedItem?.product?.title || 'Producto';

      console.log("HybridCartContext - Product added successfully:", {
        productId: variables.productId,
        addedItem,
        productName,
        totalItems: data.items.length
      });

      toast({
        title: "Producto añadido",
        description: `${productName} añadido al carrito`,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo añadir el producto al carrito",
        variant: "destructive",
      });
    },
  });

  const updateUserCartItemMutation = useMutation({
    mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
      hybridCartService.updateUserCartItem(productId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCart'] });
      toast({
        title: "Carrito actualizado",
        description: "La cantidad del producto ha sido actualizada",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo actualizar la cantidad",
        variant: "destructive",
      });
    },
  });

  const removeFromUserCartMutation = useMutation({
    mutationFn: (productId: string) => hybridCartService.removeFromUserCart(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCart'] });
      toast({
        title: "Producto eliminado",
        description: "El producto se ha eliminado del carrito",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo eliminar el producto del carrito",
        variant: "destructive",
      });
    },
  });

  const clearUserCartMutation = useMutation({
    mutationFn: hybridCartService.clearUserCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCart'] });
      toast({
        title: "Carrito vaciado",
        description: "Se han eliminado todos los productos del carrito",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo vaciar el carrito",
        variant: "destructive",
      });
    },
  });

  const clearUserCartSilentlyMutation = useMutation({
    mutationFn: hybridCartService.clearUserCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCart'] });
    },
    onError: (error) => {
      console.error('Error clearing user cart silently:', error);
    },
  });

  // ============ MERGE CART MUTATION ============

  const mergeCartMutation = useMutation({
    mutationFn: () => hybridCartService.mergeGuestCartWithUser(guestCart as GuestCart),
    onSuccess: (mergedCart) => {
      queryClient.setQueryData(['userCart'], normalizeCart(mergedCart));
      updateGuestCart(hybridCartService.getGuestCart()); // Should be empty now
      
      toast({
        title: "Carrito combinado",
        description: "Tu carrito local se ha combinado con tu cuenta",
      });
    },
    onError: () => {
      toast({
        title: "Error al combinar",
        description: "No se pudo combinar tu carrito local con tu cuenta",
        variant: "destructive",
      });
    },
  });

  // ============ SYNC GUEST CART ============

  const syncGuestCartMutation = useMutation({
    mutationFn: () => hybridCartService.syncGuestCart(),
    onSuccess: (syncedCart) => {
      updateGuestCart(syncedCart);
    },
    onError: () => {
      // Silent fail for sync - don't show error to user
      console.warn('Failed to sync guest cart with server');
    },
  });

  // ============ UNIFIED INTERFACE METHODS ============

  const addToCart = async (product: Product, quantity: number = 1, customization?: { personalizedName?: string; notes?: string }) => {
    if (isAuthenticated) {
      // Para agendas personalizadas, enviar la imagen
      const previewImage = product.image;
      await addToUserCartMutation.mutateAsync({ productId: product._id, quantity, customization, previewImage });
    } else {
      await addToGuestCart(product, quantity, customization);
    }
  };

  const updateItemQuantity = async (productId: string, quantity: number) => {
    if (isAuthenticated) {
      await updateUserCartItemMutation.mutateAsync({ productId, quantity });
    } else {
      await updateGuestCartItem(productId, quantity);
    }
  };

  const removeItem = async (productId: string) => {
    if (isAuthenticated) {
      await removeFromUserCartMutation.mutateAsync(productId);
    } else {
      await removeFromGuestCart(productId);
    }
  };

  const clearCart = async () => {
    if (isAuthenticated) {
      await clearUserCartMutation.mutateAsync();
    } else {
      await clearGuestCart();
    }
  };

  const clearCartSilently = async () => {
    if (isAuthenticated) {
      await clearUserCartSilentlyMutation.mutateAsync();
    } else {
      await clearGuestCartSilently();
    }
  };

  const mergeWithUserCart = useCallback(async (onSuccess?: () => void) => {
    if (!isAuthenticated) {
      throw new Error('User must be authenticated to merge cart');
    }
    if (!guestCart || guestCart.items.length === 0) {
      onSuccess?.(); // If there's nothing to merge, just proceed.
      return;
    }
    await mergeCartMutation.mutateAsync(undefined, {
      onSuccess: () => {
        onSuccess?.();
      }
    });
  }, [isAuthenticated, guestCart, mergeCartMutation]);

  const syncGuestCart = useCallback(async () => {
    if (isAuthenticated || guestCart.items.length === 0) {
      return; // Only sync guest carts with items
    }
    await syncGuestCartMutation.mutateAsync();
  }, [isAuthenticated, guestCart.items.length, syncGuestCartMutation]);

  // ============ COMPUTED VALUES ============

  const safeCart = cart || { items: [], totalItems: 0, subtotal: 0, total: 0, taxes: 0, isGuest: true };

  const totalItems = safeCart.totalItems || safeCart.items.length;
  const subtotal = safeCart.subtotal || 0;
  const taxes = 0; // No taxes
  const total = subtotal; // Total equals subtotal, no taxes

  const hasItems = totalItems > 0;

  const getCartItems = (): Array<GuestCartItem | UserCartItem> => {
    return safeCart.items || [];
  };

  // ============ EFFECTS ============

  // Auto-merge guest cart when user logs in
  useEffect(() => {
    if (isAuthenticated && userCart && guestCart.items.length > 0 && !mergePromptShown) {
      // Show option to merge carts
      toast({
        title: "Carrito encontrado",
        description: "Tienes productos en tu carrito local. ¿Quieres combinarlos?",
        action: (
          <button
            onClick={() => mergeWithUserCart()}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Combinar
          </button>
        ),
      });
      setMergePromptShown(true);
    }
  }, [isAuthenticated, userCart, guestCart.items.length, mergeWithUserCart, toast, mergePromptShown]);

  // Reset merge prompt when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setMergePromptShown(false);
    }
  }, [isAuthenticated]);

  // Periodic sync for guest cart (every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated && guestCart.items.length > 0) {
      const interval = setInterval(() => {
        syncGuestCart();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, guestCart.items.length, syncGuestCart]);

  // ============ CONTEXT VALUE ============

  const value: HybridCartContextType = {
    // State
    cart,
    loading,
    isGuest,
    
    // Operations
    addToCart,
    updateItemQuantity,
    removeItem,
    clearCart,
    clearCartSilently,
    
    // Hybrid Operations
    mergeWithUserCart,
    syncGuestCart,
    
    // Computed Values
    totalItems,
    subtotal,
    total,
    taxes,
    
    // Utilities
    hasItems,
    getCartItems,
  };

  return (
    <HybridCartContext.Provider value={value}>
      {children}
    </HybridCartContext.Provider>
  );
};